import { AGENT_IMAGE_MIME_TYPES } from './imageProcessing';
import type { StagedAttachment } from './types';

/** File extensions the agent can render a preview for; drives the `previewable` flag. */
const PREVIEWABLE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']);

/**
 * Serialises staged attachments (plus any caption) into the `image-upload` envelope the
 * Brief Experience service expects in a message body.
 *
 * Shape mirrors the pilot and is validated by the service's Zod schema
 * (`brief-experience` `src/lib/chat-inference/image-upload.ts`):
 *
 * - `value`: one entry per file, opaque to the service (`z.array(z.unknown())`) and echoed
 *   back verbatim on history read — so this is also the shape `parseImageUpload` reads.
 * - `images`: inline base64 for agent-supported image types only (JPEG/PNG). The service
 *   strips this array before persisting, so it never comes back on read.
 * - `message`: the caption, the only part counted against the 1000-char limit.
 */
export function buildImageUploadMessage(attachments: StagedAttachment[], caption: string): string {
    return JSON.stringify({
        type: 'image-upload',
        data: {
            value: attachments.map((att) => ({
                bucket: 'CimpressDocs',
                key: att.uploadId,
                name: att.fileName,
                previewable: PREVIEWABLE_EXTENSIONS.has(
                    att.fileName.split('.').pop()?.toLowerCase() ?? ''
                ),
                size: att.fileSize != null ? Number(att.fileSize) : undefined,
            })),
            images: attachments
                .filter((att) =>
                    AGENT_IMAGE_MIME_TYPES.has(att.compressedMediaType ?? att.mediaType)
                )
                .map((att) => ({
                    type: 'base64',
                    media_type: att.compressedMediaType ?? att.mediaType,
                    data: att.base64 ?? '',
                })),
            message: caption,
        },
    });
}
